/*eslint complexity: off*/
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import clsx from 'clsx';
import { parse } from 'date-fns';

import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';

import GridItem from '~/components/GridItem';
import GridContainer from '~/components/GridContainer';
import InputShow from '~/components/InputShow';
import InputControl from '~/components/Input/InputControl';
import InputSelect from '~/components/Input/InputSelect';
import ModalScheduler from '~/components/Scheduler';

import MedicalAppointmentsRecord from '~/records/MedicalAppointmentsRecord';
import Request from '~/providers/Request';
import isPresent from '~/utils/isPresent';
import useStyles from './styles';

const attrName = (attr) => MedicalAppointmentsRecord.hummanAttributeName(attr);

// Para o dia de hoje, descarta horarios que ja passaram.
async function buildSlot(schedule, selectedDay, intervalMin, currentTime, isToday) {
  if (!schedule.enabled) return undefined;
  const startSchedule = `${selectedDay}T${schedule.value}:00-0300`;
  const endSchedule = `${moment(startSchedule)
    .add(intervalMin, 'minutes')
    .format('YYYY-MM-DDTHH:mm:ss')}-0300`;

  if (isToday && !moment(currentTime).isBefore(endSchedule)) return undefined;

  return { startTime: new Date(startSchedule), endTime: new Date(endSchedule) };
}

// Transforma a resposta de available_hours em [{date, schedules: [{startTime,endTime}]}].
async function populateSchedule(list, intervalMin, nextMonth) {
  const slots = [];
  const currentTime = moment().format();

  for await (const day of list) {
    if (!day.enabled) continue;
    const selectedDay = day.value;
    const isToday = moment().format('L') === moment(selectedDay).format('L');
    const isGreaterToday = moment().format('L') < moment(selectedDay).format('L');

    if (isGreaterToday || isToday || nextMonth) {
      const daySchedules = [];
      for await (const schedule of day.hours) {
        if (schedule.enabled) {
          daySchedules.push(
            await buildSlot(schedule, selectedDay, intervalMin, currentTime, isToday)
          );
        }
      }
      slots.push({ date: day.value, schedules: daySchedules });
    }
  }
  return slots;
}

/**
 * Bloco de agendamento compartilhado (new + edit): especialidade -> medico ->
 * modal de horarios. O horario exibido e SEMPRE derivado do registro, entao ao
 * abrir o edit o horario ja marcado aparece (corrige o "campo em branco"). So
 * zera quando o usuario troca DELIBERADAMENTE a especialidade.
 */
function AppointmentScheduler({ record, setRecord, disabled }) {
  const classes = useStyles();
  const { t } = useTranslation();

  const [specialtiesOptions, setSpecialtiesOptions] = useState([]);
  const [doctorsOptions, setDoctorsOptions] = useState([]);
  const [scheduleDates, setScheduleDates] = useState([]);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [intervalMin, setIntervalMin] = useState(0);
  const [slotInterval, setSlotInterval] = useState(null);

  const specialty = record.get('specialty');
  const doctor = record.get('doctor');
  const specialtyId = specialty && specialty.id;
  const doctorId = doctor && doctor.id;
  const userId = (record.get('user') || {}).id;
  const dateRecord = record.get('schedule_appointment_at');
  const doctorsRequest = useRef(0);
  const previousUserId = useRef(userId);

  const slot = `${dateRecord}|${doctorId}|${specialtyId}`;
  const loadedSlot = useRef(null);
  if (loadedSlot.current === null && record.isPersisted() && dateRecord)
    loadedSlot.current = slot;
  const storedDuration = Number(record.get('appointment_duration'));
  const endMinutes =
    storedDuration && slotInterval && slot === loadedSlot.current
      ? storedDuration + slotInterval
      : intervalMin;

  const scheduleDisplay = dateRecord
    ? `${moment(dateRecord).format('DD/MM/YYYY HH:mm')}${
        endMinutes
          ? ` - ${moment(dateRecord).add(endMinutes, 'minute').format('HH:mm')}`
          : ''
      }`
    : '';

  async function fetchSpecialties() {
    Request.get('/admin/specialties', null, { normalizeJSON: true })
      .then((r) => setSpecialtiesOptions(r.data))
      .catch(() => setSpecialtiesOptions([]));
  }

  async function fetchDoctors(idSpec) {
    const request = ++doctorsRequest.current;
    const userChanged =
      previousUserId.current !== userId &&
      !(record.isPersisted() && previousUserId.current === undefined);
    previousUserId.current = userId;

    Request.getNotDeserialize(
      '/admin/schedules/doctors_by_specialty',
      userId ? { specialty_id: idSpec, user_id: userId } : { specialty_id: idSpec },
      { normalizeJSON: true }
    )
      .then((response) => {
        if (request !== doctorsRequest.current) return;
        const items = response.data.map((u) => ({
          id: Number(u.user_id),
          name: `${u.user_name}${
            isPresent(u.specialty_name)
              ? ` <${u.specialty_name}>`
              : ' <Clínico Geral>'
          }`,
        }));
        setDoctorsOptions([
          ...items,
          { id: null, name: t('medicalAppointments.extras.no_doctor') },
        ]);
        if (userChanged) {
          setRecord((prev) => {
            const current = prev.get('doctor');
            if (!current || items.some((item) => item.id === Number(current.id))) {
              return prev;
            }
            return prev.updateAttributes({ doctor: null, schedule_appointment_at: null });
          });
        }
      })
      .catch(() => setDoctorsOptions([]));
  }

  async function fetchSchedule(specId, docId) {
    const filter = specialtiesOptions.find((i) => i.id === specId);
    if (!filter || !docId) return;

    setLoadingSchedule(true);
    const interval =
      Number(filter.appointment_interval) + Number(filter.appointment_duration);
    setIntervalMin(interval);

    const url = '/admin/schedules/available_hours';
    const monthYear = moment(new Date()).format('yyyy-MM');
    const nextMonth = moment(new Date()).add(1, 'month').format('yyyy-MM');

    try {
      const [m1, m2] = await Promise.all([
        Request.getNotDeserialize(
          url,
          { year_month: monthYear, specialty_id: specId, doctor_id: docId },
          { normalizeJSON: false }
        ),
        Request.getNotDeserialize(
          url,
          { year_month: nextMonth, specialty_id: specId, doctor_id: docId },
          { normalizeJSON: false }
        ),
      ]);
      const slotMinutes = m1.slot_minutes || interval;
      setIntervalMin(slotMinutes);
      setSlotInterval(m1.slot_interval || null);
      const d1 = await populateSchedule(m1.data, slotMinutes, false);
      const d2 = await populateSchedule(m2.data, slotMinutes, true);
      setScheduleDates(d1.concat(d2));
    } catch (e) {
      setScheduleDates([]);
    } finally {
      setLoadingSchedule(false);
    }
  }

  // especialidades no mount
  useEffect(() => {
    fetchSpecialties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // medicos quando a especialidade muda (inclui a especialidade ja gravada no edit)
  useEffect(() => {
    if (specialtyId) fetchDoctors(specialtyId);
    else setDoctorsOptions([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specialtyId, userId]);

  // slots quando especialidade + medico estao definidos (carrega tambem no edit)
  useEffect(() => {
    if (specialtyId && doctorId && specialtiesOptions.length > 0) {
      fetchSchedule(specialtyId, doctorId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specialtyId, doctorId, specialtiesOptions.length]);

  // usuario escolheu um slot no modal
  useEffect(() => {
    if (scheduleDate && scheduleDate.length > 0) {
      const iso = parse(scheduleDate, 'dd/MM/yyyy HH:mm', new Date()).toISOString();
      setRecord(
        record.updateAttributes({
          schedule_appointment_at: iso,
          status: 'on_schedule',
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleDate]);

  // troca DELIBERADA de especialidade -> zera medico + horario (so via onChange)
  const handleSpecialty = (sp) => {
    setScheduleDates([]);
    setRecord(
      record.updateAttributes({
        specialty: sp || null,
        doctor: null,
        schedule_appointment_at: null,
      })
    );
  };

  const handleDoctor = (dc) => {
    setRecord(
      record.updateAttributes({
        doctor: dc || null,
        schedule_appointment_at: null,
      })
    );
  };

  return (
    <>
      <ModalScheduler
        modal={modalOpen}
        onClose={() => setModalOpen(false)}
        isLoading={loadingSchedule}
        scheduleDates={scheduleDates}
        setScheduleDate={setScheduleDate}
      />
      <GridContainer>
        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputControl
            name={'specialty'}
            value={specialty}
            label={attrName('specialty')}
            required
            disabled={disabled || loadingSchedule}
            inputComponent={InputSelect}
            onChange={({ specialty: sp }) => handleSpecialty(sp)}
            InputProps={{
              isClearable: false,
              options: specialtiesOptions,
              outValueType: 'object',
            }}
          />
        </GridItem>
        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <InputControl
            name={'doctor'}
            value={doctor}
            label={attrName('doctor')}
            disabled={disabled || loadingSchedule || !specialtyId}
            inputComponent={InputSelect}
            onChange={({ doctor: dc }) => handleDoctor(dc)}
            InputProps={{
              isClearable: false,
              options: doctorsOptions,
              outValueType: 'object',
            }}
          />
        </GridItem>
        <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
          <Button
            className={
              loadingSchedule
                ? clsx(classes.inputSheduleLoading)
                : clsx(classes.inputShedule)
            }
            onClick={() => setModalOpen(true)}
            disabled={disabled || loadingSchedule || !doctorId}
          >
            <InputShow
              label={
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                  {attrName('schedule_appointment_at')}
                  {loadingSchedule && (
                    <CircularProgress size={11} style={{ marginLeft: 6 }} />
                  )}
                </span>
              }
              value={scheduleDisplay}
              name="schedule_appointment_at"
            />
          </Button>
        </GridItem>
      </GridContainer>
    </>
  );
}

export default AppointmentScheduler;
